import type { DocumentData } from 'firebase/firestore';

export const MAINTENANCE_HUB_IDS = ['business', 'dipendenze', 'documenti', 'applicativi', 'hr'] as const;
export type MaintenanceHubId = typeof MAINTENANCE_HUB_IDS[number];

export interface MaintenanceActor {
  uid: string | null;
  email: string | null;
}

export interface MaintenanceScopeState {
  enabled: boolean;
  message: string | null;
  estimatedRestoreAt: string | null;
  activatedAt: string | null;
  activatedBy: MaintenanceActor | null;
  updatedAt: string | null;
  updatedBy: MaintenanceActor | null;
}

export interface MaintenanceState {
  schemaVersion: 2;
  global: MaintenanceScopeState;
  hubs: Record<MaintenanceHubId, MaintenanceScopeState>;
  enabled: boolean;
  message: string | null;
  estimatedRestoreAt: string | null;
  activatedAt: string | null;
  activatedBy: MaintenanceActor | null;
  updatedAt: string | null;
  updatedBy: MaintenanceActor | null;
}

export interface MaintenanceUpdate {
  enabled?: boolean;
  message?: string | null;
  estimatedRestoreAt?: string | null;
}

function offScope(): MaintenanceScopeState {
  return {
    enabled: false,
    message: null,
    estimatedRestoreAt: null,
    activatedAt: null,
    activatedBy: null,
    updatedAt: null,
    updatedBy: null,
  };
}

export const OFF_MAINTENANCE_STATE: MaintenanceState = {
  schemaVersion: 2,
  global: offScope(),
  hubs: {
    business: offScope(),
    dipendenze: offScope(),
    documenti: offScope(),
    applicativi: offScope(),
    hr: offScope(),
  },
  enabled: false,
  message: null,
  estimatedRestoreAt: null,
  activatedAt: null,
  activatedBy: null,
  updatedAt: null,
  updatedBy: null,
};

function toIso(value: unknown): string | null {
  if (!value) return null;
  if (typeof value === 'string') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  }
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.toISOString();
  if (typeof value === 'object' && value !== null && 'toDate' in value && typeof value.toDate === 'function') {
    const date = value.toDate();
    return date instanceof Date && !Number.isNaN(date.getTime()) ? date.toISOString() : null;
  }
  return null;
}

function normalizeActor(value: unknown): MaintenanceActor | null {
  if (!value || typeof value !== 'object') return null;
  const actor = value as { uid?: unknown };
  if (typeof actor.uid !== 'string' || !actor.uid) return null;
  return { uid: actor.uid, email: null };
}

function normalizeScopeState(data?: DocumentData): MaintenanceScopeState {
  return {
    enabled: data?.enabled === true,
    message: typeof data?.message === 'string' && data.message.length <= 1000 && data.message.trim() ? data.message : null,
    estimatedRestoreAt: toIso(data?.estimatedRestoreAt),
    activatedAt: toIso(data?.activatedAt),
    activatedBy: normalizeActor(data?.activatedBy),
    updatedAt: toIso(data?.updatedAt),
    updatedBy: normalizeActor(data?.updatedBy),
  };
}

function legacyMirror(global: MaintenanceScopeState) {
  return {
    enabled: global.enabled,
    message: global.message,
    estimatedRestoreAt: global.estimatedRestoreAt,
    activatedAt: global.activatedAt,
    activatedBy: global.activatedBy,
    updatedAt: global.updatedAt,
    updatedBy: global.updatedBy,
  };
}

export function parseMaintenanceData(data?: DocumentData): MaintenanceState {
  const raw = data || {};
  const hasSchemaVersion = Object.prototype.hasOwnProperty.call(raw, 'schemaVersion');
  const schemaVersion = raw.schemaVersion;
  if (hasSchemaVersion && schemaVersion !== 1 && schemaVersion !== 2) {
    throw new Error('Schema manutenzione non supportato');
  }
  const isV2 = schemaVersion === 2;
  if (isV2 && (!raw.global || typeof raw.global !== 'object' || Array.isArray(raw.global)
    || !raw.hubs || typeof raw.hubs !== 'object' || Array.isArray(raw.hubs))) {
    throw new Error('Documento manutenzione v2 non valido');
  }
  const nestedGlobal = isV2 && raw.global && typeof raw.global === 'object' && !Array.isArray(raw.global)
    ? raw.global as DocumentData
    : null;
  const global = normalizeScopeState(isV2 ? { ...raw, ...(nestedGlobal || {}) } : raw);
  const rawHubs = isV2 && raw.hubs && typeof raw.hubs === 'object' && !Array.isArray(raw.hubs)
    ? raw.hubs as Record<string, DocumentData>
    : {};
  const hubs = Object.fromEntries(
    MAINTENANCE_HUB_IDS.map((hubId) => [hubId, normalizeScopeState(rawHubs[hubId])]),
  ) as Record<MaintenanceHubId, MaintenanceScopeState>;

  return {
    schemaVersion: 2,
    global,
    hubs,
    ...legacyMirror(global),
  };
}
